/**
 * Redux slice for business profile information
 * Manages company details, registration, and business classification
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface BizBorState {
  company: string | null;
  regNumber: string | null;
  legalStructure: string | null;
  legalStructureOthers: string | null;
  industryGroup: string | null;
  industryDropdownValues: any | null;
  typeOfInstitute: string | null;
  industryOthers: string | null;
  countryOfIncorp: string | null;
  countryOfDomicile: string | null;
  address: string | null;
  postal: string | null;
  website: string | null;
  phoneNumber: string | null;
  companyStartDate: string | null;
}

const initialState: BizBorState = {
  company: null,
  regNumber: null,
  legalStructure: null,
  legalStructureOthers: null,
  industryGroup: null,
  industryDropdownValues: null,
  typeOfInstitute: null,
  industryOthers: null,
  countryOfIncorp: null,
  countryOfDomicile: null,
  address: null,
  postal: null,
  website: null,
  phoneNumber: null,
  companyStartDate: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof BizBorState>(field: K) => {
  return (state: BizBorState, action: PayloadAction<BizBorState[K]>) => {
    state[field] = action.payload;
  };
};

const bizBorSlice = createSlice({
  name: "borBiz",
  initialState,
  reducers: {
    // Company identification
    setCompanyRedux: createFieldSetter("company"),
    setRegNumberRedux: createFieldSetter("regNumber"),
    setCompanyStartDateRedux: createFieldSetter("companyStartDate"),

    // Legal structure
    setLegalStructureRedux: createFieldSetter("legalStructure"),
    setLegalStructureOthersRedux: createFieldSetter("legalStructureOthers"),
    setTypeOfInstituteRedux: createFieldSetter("typeOfInstitute"),

    // Industry classification
    setIndustryGroupRedux: createFieldSetter("industryGroup"),
    setIndustryDropdownValuesRedux: createFieldSetter("industryDropdownValues"),
    setIndustryOthersRedux: createFieldSetter("industryOthers"),

    // Location and contact
    setCountryOfIncorpRedux: createFieldSetter("countryOfIncorp"),
    setCountryOfDomicileRedux: createFieldSetter("countryOfDomicile"),
    setAddressRedux: createFieldSetter("address"),
    setPostalRedux: createFieldSetter("postal"),
    setWebsiteRedux: createFieldSetter("website"),
    setPhoneNumber: createFieldSetter("phoneNumber"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateBizState: (state, action: PayloadAction<Partial<BizBorState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetBizState: () => initialState,
  },
});

export const {
  setCompanyRedux,
  setRegNumberRedux,
  setLegalStructureRedux,
  setLegalStructureOthersRedux,
  setTypeOfInstituteRedux,
  setIndustryGroupRedux,
  setIndustryDropdownValuesRedux,
  setIndustryOthersRedux,
  setCountryOfIncorpRedux,
  setCountryOfDomicileRedux,
  setPostalRedux,
  setWebsiteRedux,
  setAddressRedux,
  setPhoneNumber,
  setCompanyStartDateRedux,
  hydrateBizState,
  resetBizState,
} = bizBorSlice.actions;

export default bizBorSlice.reducer;
